import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Header } from "../components/Sidebar";
import { useAuth } from "../context/AuthContext";
import api, { apiErr } from "../lib/api";
import { formatGs, ROLE_LABELS, pyToday } from "../lib/format";
import { Wallet, CheckCircle2, History, Clock } from "lucide-react";
import { CodeChip } from "../components/EnvioDetalle";
import { SearchBox, matchQ } from "../components/SearchBox";
import { toast } from "sonner";

export default function Rendiciones() {
  const { setMobileOpen } = useOutletContext();
  const { user } = useAuth();
  const canClose = ["admin", "encargado", "encargado_comisionista"].includes(user?.role);
  const [tab, setTab] = useState("pendientes");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [rec, setRec] = useState({ total: 0, cantidad: 0, rendiciones: [] });
  const [fecha, setFecha] = useState(pyToday());

  const load = useCallback(() => { api.get("/rendiciones").then(({ data }) => setRows(data)).catch(() => {}); }, []);
  const loadRec = useCallback(() => {
    api.get(`/rendiciones/recibidas?fecha=${fecha}`).then(({ data }) => setRec(data)).catch(() => {});
  }, [fecha]);
  useEffect(() => { if (tab === "pendientes") load(); else loadRec(); }, [tab, load, loadRec]);

  const cerrar = async (cobrador_id) => {
    try {
      const { data } = await api.post(`/rendiciones/${cobrador_id}/cerrar`);
      toast.success(`Rendición recibida: ${data.envios_rendidos} envíos · ${formatGs(data.total)}`);
      load();
    }
    catch (e) { toast.error(apiErr(e)); }
  };

  const total = rows.reduce((s, r) => s + r.total, 0);
  const inputCls = "rounded-lg border border-slate-300 px-3 py-1.5 text-sm outline-none focus:border-[#3B82F6]";

  return (
    <>
      <Header title="Rendiciones" subtitle="Monto a rendir por cobrador" onMenu={() => setMobileOpen(true)} />
      <main className="p-4 sm:p-6 lg:p-8 space-y-5">
        {canClose && (
          <div className="flex gap-2" data-testid="rend-tabs">
            <button onClick={() => setTab("pendientes")} data-testid="rend-tab-pendientes" className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold border ${tab === "pendientes" ? "bg-[#0A1F44] text-white border-[#0A1F44]" : "bg-white text-[#0A1F44] border-slate-300"}`}><Clock className="h-4 w-4" /> Pendientes</button>
            <button onClick={() => setTab("recibidas")} data-testid="rend-tab-recibidas" className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold border ${tab === "recibidas" ? "bg-[#0A1F44] text-white border-[#0A1F44]" : "bg-white text-[#0A1F44] border-slate-300"}`}><History className="h-4 w-4" /> Recibidas</button>
          </div>
        )}

        {tab === "pendientes" ? (
          <>
            <div className="bg-white rounded-xl border border-[#E2E8F0] p-5 shadow-sm flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center"><Wallet className="h-5 w-5 text-[#F5A623]" /></div>
                <div>
                  <div className="text-xs text-[#64748B] font-medium uppercase tracking-wide">Total no rendido</div>
                  <div className="text-2xl font-extrabold text-[#0A1F44]">{formatGs(total)}</div>
                </div>
              </div>
            </div>

            <SearchBox value={q} onChange={setQ} placeholder="Buscar cobrador, sucursal o código…" testid="rend-search" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {rows.filter(matchQ(q)).map((r) => (
                <div key={r.cobrador_id} className="bg-white rounded-xl border border-[#E2E8F0] p-5 shadow-sm" data-testid={`rendicion-card-${r.cobrador_id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-bold text-[#0A1F44]">{r.nombre}</div>
                      <div className="text-xs text-[#64748B]">{ROLE_LABELS[r.role] || r.role}{r.sucursal ? ` · ${r.sucursal}` : ""} · {r.cantidad} entregas</div>
                    </div>
                    <div className="text-xl font-extrabold text-[#0A1F44]">{formatGs(r.total)}</div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {r.envios.map((c) => <CodeChip key={c} code={c} />)}
                  </div>
                  {canClose && (
                    <button onClick={() => cerrar(r.cobrador_id)} data-testid={`cerrar-rendicion-${r.cobrador_id}`} className="mt-4 w-full flex items-center justify-center gap-2 text-sm font-semibold bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg py-2">
                      <CheckCircle2 className="h-4 w-4" /> Recibir y aprobar rendición
                    </button>
                  )}
                </div>
              ))}
              {rows.length === 0 && <div className="col-span-full text-center py-12 text-[#64748B] bg-white rounded-xl border border-[#E2E8F0]">No hay montos pendientes de rendir.</div>}
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-wrap items-end gap-3 bg-white rounded-xl border border-[#E2E8F0] p-4 shadow-sm">
              <div><label className="block text-[11px] font-semibold text-[#64748B] mb-1">Fecha</label><input type="date" className={inputCls} value={fecha} onChange={(e) => setFecha(e.target.value)} data-testid="rend-fecha-input" /></div>
              <div><label className="block text-[11px] font-semibold text-[#64748B] mb-1">Buscar</label><input className={inputCls} placeholder="Cobrador o código" value={q} onChange={(e) => setQ(e.target.value)} data-testid="rend-rec-search" /></div>
              <button onClick={loadRec} data-testid="rend-filtrar-btn" className="rounded-lg bg-[#0A1F44] text-white px-4 py-1.5 text-sm font-semibold">Filtrar</button>
              <div className="ml-auto text-right">
                <div className="text-[11px] text-[#64748B] uppercase">Total recibido en el día</div>
                <div className="text-xl font-extrabold text-emerald-600" data-testid="rend-total-dia">{formatGs(rec.total)}</div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[#64748B] text-xs uppercase">
                  <tr>
                    <th className="text-left font-semibold px-4 py-3">Hora</th>
                    <th className="text-left font-semibold px-4 py-3">Cobrador</th>
                    <th className="text-center font-semibold px-4 py-3">Envíos</th>
                    <th className="text-right font-semibold px-4 py-3">Total</th>
                    <th className="text-left font-semibold px-4 py-3 hidden sm:table-cell">Recibido por</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rec.rendiciones.filter(matchQ(q)).map((r) => (
                    <tr key={r.id} className="hover:bg-slate-50/60" data-testid={`rend-rec-row-${r.id}`}>
                      <td className="px-4 py-3 font-medium text-[#0A1F44] tabular-nums">{(r.created_at || "").slice(11, 16)}</td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-[#0A1F44]">{r.cobrador_nombre}</div>
                        <div className="text-xs text-[#64748B]">{ROLE_LABELS[r.cobrador_role] || r.cobrador_role || "—"}{r.sucursal ? ` · ${r.sucursal}` : ""}</div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="tabular-nums font-semibold">{r.cantidad}</span>
                        <div className="mt-1 flex flex-wrap justify-center gap-1">
                          {(r.envios || []).map((c) => <CodeChip key={c} code={c} />)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-[#0A1F44]">{formatGs(r.total)}</td>
                      <td className="px-4 py-3 hidden sm:table-cell text-[#64748B]">{r.recibido_by}</td>
                    </tr>
                  ))}
                  {rec.rendiciones.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-[#64748B]">No hay rendiciones recibidas en esa fecha.</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </>
  );
}
